/**
 * CellStage — Phase 1, "Unicell".
 *
 * Wires up all systems for the microscopic stage. This is the template for
 * future stages (Stage 2 "Multicell", Stage 3 "Aquatic", etc.):
 *
 *   1. init(ctx): create layers, instantiate systems, hook inputs, restore
 *      any previous run state from the save manager
 *   2. update(dt, elapsed): tick every system in dependency order and check
 *      win/loss conditions
 *   3. cleanup(): unhook listeners and drop DOM/Pixi objects
 *
 * Adding a new stage: create /src/stages/<id>/<Id>Stage.ts exporting a class
 * with the same three methods, then register it in Game.ts. StageManager
 * handles swapping.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import type { Stage, StageContext } from '@/core/StageManager';
import { stageManager } from '@/core/StageManager';
import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { IS_TOUCH } from '@/ui/TouchControls';
import { player } from '@/systems/PlayerState';

import { MovementSystem } from '@/systems/MovementSystem';
import { AbsorptionSystem } from '@/systems/AbsorptionSystem';
import { SpawnSystem } from '@/systems/SpawnSystem';
import { ParticleSystem } from '@/systems/ParticleSystem';
import { ComboSystem } from '@/systems/ComboSystem';
import { UpgradeSystem } from '@/systems/UpgradeSystem';
import { MutationSystem } from '@/systems/MutationSystem';
import { EnvironmentSystem } from '@/systems/EnvironmentSystem';
import { CodexSystem } from '@/systems/CodexSystem';
import { ApexHuntSystem } from '@/systems/ApexHuntSystem';
import { BioTurbiditySystem } from '@/systems/BioTurbiditySystem';
import { ProteinCrystalSystem } from '@/systems/ProteinCrystalSystem';
import { AchievementSystem } from '@/systems/AchievementSystem';
import { MetaUpgradeState } from '@/systems/MetaUpgradeState';

import { createCell } from '@/entities/createCell';
import { createFood } from '@/entities/createFood';
import { createPredator } from '@/entities/createPredator';
import { Absorbable, Hostile, PlayerCell, Position, Velocity } from '@/components';

import { VoronoiFilter } from '@/shaders/voronoi';
import { ChromaticAberrationFilter } from '@/shaders/chromaticAberration';
import { UI } from '@/ui/UI';
import { buildCodexIcons } from '@/visuals/codexIcons';
import { CodexSpotlight, type SpotlightTarget } from '@/ui/CodexSpotlight';

// Win is now biomass-only — `time` is kept on the object so the HUD still
// has a "nominal target time" reference for context, but elapsed counts
// up freely and only modulates the carryover bonus on completion.
const STAGE_TARGET = { biomass: 250_000, time: 30 * 60 };

/**
 * Speed-bonus tiers. Faster completion = bigger DNA carry into Phase 2.
 * Tiers are spaced so a "good" run (~25min) is the rough centre; sub-15
 * is genuinely fast, 40+ is "took your time, that's fine, smaller bonus".
 */
function speedTierFor(elapsedSec: number): { mult: number; label: string } {
  const min = elapsedSec / 60;
  if (min < 15) return { mult: 3.0, label: 'Rapid evolution' };
  if (min < 25) return { mult: 2.0, label: 'Swift evolution' };
  if (min < 40) return { mult: 1.0, label: 'Steady evolution' };
  return { mult: 0.5, label: 'Patient evolution' };
}

/**
 * Camera zoom: scales the entity/fx layers around the screen centre. The
 * world is screen / zoom, so it always fills the screen — zooming out
 * shows more water rather than shrinking the play field.
 *
 * Phones show a fraction of a desktop window's pixels, so they always
 * start zoomed out; desktop always starts at 1. It is not remembered
 * between runs — a pinch lasts for the current run only.
 */
const TOUCH_DEFAULT_ZOOM = 0.5;

function startZoom(): number {
  return IS_TOUCH ? TOUCH_DEFAULT_ZOOM : 1;
}

export class CellStage implements Stage {
  readonly id = 'cell';

  private ctx!: StageContext;
  private entityLayer!: Container;
  private fxLayer!: Container;
  private bg!: Sprite;
  private voronoi!: VoronoiFilter;
  private chroma!: ChromaticAberrationFilter;
  private chromaStrength = 0;
  private flashSprite: Sprite | null = null;
  private flashRemaining = 0;
  private flashAlpha = 0;

  private movement!: MovementSystem;
  private absorption!: AbsorptionSystem;
  private spawn!: SpawnSystem;
  private particles!: ParticleSystem;
  private combo!: ComboSystem;
  private upgrades!: UpgradeSystem;
  private mutations!: MutationSystem;
  private environment!: EnvironmentSystem;
  private codex!: CodexSystem;
  private apexHunt!: ApexHuntSystem;
  private turbidity!: BioTurbiditySystem;
  private crystals!: ProteinCrystalSystem;
  private achievements!: AchievementSystem;
  private metaUpgrades!: MetaUpgradeState;
  private ui!: UI;
  private spotlight!: CodexSpotlight;

  private playerEntity = 0;
  private running = true;
  private deadTimer = 0;

  // --- new-game intro: phased reveal so a fresh start doesn't dump cell,
  // food, and predators on screen all at once. Inactive on death-restart
  // and on resume from save.
  private introMode = false;
  private introT = 0;
  private introBeats: { at: number; fired: boolean; fn: () => void }[] = [];

  private lastPlayerX = 0;
  private lastPlayerY = 0;
  // last pointer position in WORLD coords (not CSS) — used as the dash target
  private lastPointerX = 0;
  private lastPointerY = 0;
  private worldZoom = 1;

  private removers: (() => void)[] = [];

  async init(ctx: StageContext): Promise<void> {
    this.ctx = ctx;
    const { app, world, data } = ctx;

    // Backing track — the licensed mp3, kept low so SFX cut through.
    // AudioManager fades in on first user gesture; same track plays
    // across all stages.
    bus.emit('music:setTrack', { id: 'main' });

    // --- background (voronoi filter on a plain white sprite) ---
    this.bg = new Sprite(Texture.WHITE);
    this.bg.tint = 0x040a18;
    this.bg.width = app.screen.width;
    this.bg.height = app.screen.height;
    this.voronoi = new VoronoiFilter();
    this.bg.filters = [this.voronoi];
    app.stage.addChildAt(this.bg, 0);

    // --- world + fx layers (child of the context-provided layers) ---
    this.entityLayer = new Container();
    this.fxLayer = new Container();
    ctx.worldLayer.addChild(this.entityLayer);
    ctx.fxLayer.addChild(this.fxLayer);

    // Camera zoom — phones start zoomed out, desktop at 1.
    this.applyZoom(startZoom());

    // Debug hook (same spirit as window.__evolve in main.ts) — lets a
    // headless run read world size, zoom and the player's position.
    (window as any).__cell = this;

    // --- post filter on the world layer: chromatic aberration ---
    this.chroma = new ChromaticAberrationFilter();
    ctx.worldLayer.filters = [this.chroma];

    // --- systems ---
    this.combo = new ComboSystem();
    this.mutations = new MutationSystem(data.mutations, data.mutationFamilies);
    this.environment = new EnvironmentSystem(data.events);
    this.mutations.setEnvironment(this.environment);
    this.upgrades = new UpgradeSystem(data.upgrades, this.mutations);
    this.codex = new CodexSystem(data.codex);
    this.movement = new MovementSystem(this.mutations);
    this.absorption = new AbsorptionSystem(this.mutations, this.environment, this.combo, data.predators, this.entityLayer);
    this.spawn = new SpawnSystem(world, this.entityLayer, data.foods, data.predators, this.environment);
    this.apexHunt = new ApexHuntSystem(world, this.entityLayer, data.predators);
    this.particles = new ParticleSystem(this.fxLayer);
    this.turbidity = new BioTurbiditySystem(
      ctx.worldLayer, app.stage, app.screen.width, app.screen.height,
    );
    this.crystals = new ProteinCrystalSystem(ctx.worldLayer);
    this.metaUpgrades = new MetaUpgradeState(data.metaUpgrades);
    this.achievements = new AchievementSystem(data.achievements);

    // Heritage Tree affects BASE_STATS — install before recompute so the
    // first stats resolution sees the meta baseline.
    player.setMetaUpgrades(this.metaUpgrades);

    // --- state hydration ---
    player.hydrateFromSave();
    this.metaUpgrades.applyStartingSlots();
    this.metaUpgrades.applyRunStartBonuses();
    this.metaUpgrades.notifyStageStart();
    this.achievements.notifyStageStart('cell');
    this.upgrades.recompute();
    if (player.hp <= 0) player.healTo(player.stats.hpMax);
    if (player.atp <= 0) player.regenAtp(player.stats.atpMax);

    // --- create entities ---
    const vw0 = this.worldW;
    const vh0 = this.worldH;
    this.playerEntity = createCell(world, this.entityLayer, vw0 / 2, vh0 / 2);

    // Fresh start? Run the scripted intro that introduces the cell, then
    // food, then a predator, with a codex pop for each. On a resumed run
    // (continue, or restart-after-death), skip straight to normal spawn.
    if (!saveManager.hasRunProgress()) {
      this.beginIntro(vw0, vh0, ctx.data.foods, ctx.data.predators);
    } else {
      bus.emit('codex:unlocked', { entryId: 'concept.welcome' });
      this.spawn.spawnInitial(vw0, vh0);
    }

    // --- codex icons (Pixi-extracted bitmaps of the same biology art used
    // in-game) — built once, passed to UI so codex entries show the same
    // silhouette the player encounters.
    const codexIcons = buildCodexIcons(app, data.foods, data.predators);

    // --- UI ---
    this.ui = new UI({
      upgrades: this.upgrades,
      mutations: this.mutations,
      codex: this.codex,
      environment: this.environment,
      events: data.events,
      stageTarget: STAGE_TARGET,
      codexIcons,
      restart: () => this.restart('ui-button'),
      onPulse: () => this.abilityPulse(),
      onDash: () => this.abilityDash(),
    });

    // First-encounter spotlight tutorial. Phase 1 entities live in
    // screen space (no camera), so visibility = position within screen
    // bounds. Finder picks the first live Absorbable / Hostile whose
    // codexId matches and is currently visible — returning null skips
    // the popup for that unlock.
    this.spotlight = new CodexSpotlight(
      app, this.fxLayer, ctx.data.codex,
      (codexId) => this.findVisibleEntityByCodexId(codexId),
      codexIcons,
    );

    // --- input ---
    this.setupInput();

    // --- listeners ---
    this.removers.push(bus.on('combo:milestone', ({ combo }) => {
      if (combo >= 10) this.chromaStrength = Math.max(this.chromaStrength, 1.0);
    }));
    this.removers.push(bus.on('bloom:cleared', ({ x, y, shape, tier }) => {
      // Main shape clear: small DNA sip + combo advance.
      // Satellite clear: timer extension only — DNA must be earned, not sprinkled.
      if (tier === 'main') {
        const bonus = 1;
        player.addDna(bonus);
        bus.emit('fx:floatingText', { x, y, text: `+${bonus} DNA · ${shape} cleared`, color: 0xB794F4 });
        bus.emit('fx:particles', { x, y, count: 24, color: 0xB794F4, spread: 80 });
        bus.emit('audio:cue', { id: 'bloomCleared' });
      } else {
        bus.emit('fx:floatingText', { x, y, text: `+2s`, color: 0x9ae6b4 });
        bus.emit('fx:particles', { x, y, count: 10, color: 0x9ae6b4, spread: 50 });
        bus.emit('audio:cue', { id: 'satelliteClear' });
      }
    }));
    this.removers.push(bus.on('fx:flash', ({ color, alpha, duration }) => {
      this.triggerFlash(color, alpha, duration);
    }));
    this.removers.push(bus.on('mutation:picked', () => {
      this.mutations.markDirty();
      this.upgrades.recompute();
    }));
    this.removers.push(bus.on('mutation:ranked', () => {
      this.mutations.markDirty();
      this.upgrades.recompute();
    }));
    this.removers.push(bus.on('dna:changed', ({ value }) => {
      saveManager.getMeta().dna = value;
    }));
    this.removers.push(bus.on('player:died', () => {
      console.warn('[CellStage] player:died — biomass=', player.biomass, 'hp=', player.hp);
      this.running = false;
      this.deadTimer = 2.5;
      bus.emit('fx:flash', { color: 0xfc8181, alpha: 0.35, duration: 0.6 });
      bus.emit('fx:screenShake', { intensity: 14, duration: 0.45 });
    }));
    // Streak reward: every 10th same-food in a chain drops a DNA strand
    // somewhere on screen, away from the player so the player has to go
    // grab it. The strand is a normal DNA food entity, so it gets
    // absorbed through the existing food path.
    this.removers.push(bus.on('combo:streakReward', () => {
      this.dropStreakDna();
    }));
  }

  /** Spawn a DNA strand at a random on-screen position, away from the
   *  player's current position. Triggered by streak milestones. */
  private dropStreakDna(): void {
    const { world, app } = this.ctx;
    const dnaDef = this.ctx.data.foods['dna'];
    if (!dnaDef) return;
    const w = this.worldW;
    const h = this.worldH;
    const margin = 60;
    const ppos = world.get(this.playerEntity, Position);
    let x = 0, y = 0;
    // try a few placements, pick one that's far enough from the cell
    for (let i = 0; i < 6; i++) {
      x = margin + Math.random() * (w - margin * 2);
      y = margin + Math.random() * (h - margin * 2);
      if (!ppos) break;
      const dx = x - ppos.x, dy = y - ppos.y;
      if (dx * dx + dy * dy > 220 * 220) break;
    }
    createFood(world, this.entityLayer, dnaDef, x, y);
    bus.emit('fx:floatingText', { x, y: y - 20, text: 'DNA dropped!', color: 0xB794F4 });
    bus.emit('fx:particles', { x, y, count: 14, color: 0xB794F4, spread: 60 });
    bus.emit('audio:cue', { id: 'codex', volume: 0.4 });
  }

  update(dt: number, _elapsed: number): void {
    const { app, world } = this.ctx;
    const w = app.screen.width, h = app.screen.height;
    // Virtual (world-coord) size that systems see. Everything inside
    // entityLayer/fxLayer renders at `worldZoom` × world coords, so the
    // world that exactly fills the screen is screen / zoom.
    const vw = w / this.worldZoom, vh = h / this.worldZoom;

    // background (covers the full real screen)
    this.bg.width = w; this.bg.height = h;
    this.voronoi.update(dt, w, h);

    // chromatic aberration shimmer decay
    this.chromaStrength = Math.max(0, this.chromaStrength - dt * 1.8);
    this.chroma.setStrength(this.chromaStrength);

    // flash overlay
    if (this.flashRemaining > 0) {
      this.flashRemaining -= dt;
      const t = Math.max(0, this.flashRemaining) / 0.25;
      if (this.flashSprite) this.flashSprite.alpha = this.flashAlpha * t;
      if (this.flashRemaining <= 0 && this.flashSprite) this.flashSprite.alpha = 0;
    }

    // Bioturbidity escalates with biomass: nothing until 10K, ramping to
    // full at 125K (~half the stage target). The murk reads as the petri
    // dish getting biologically denser as the cell takes more material in.
    const TURBIDITY_START = 10_000;
    const TURBIDITY_FULL = 125_000;
    const intensity = Math.max(0, Math.min(1,
      (player.biomass - TURBIDITY_START) / (TURBIDITY_FULL - TURBIDITY_START),
    ));
    this.turbidity.setIntensity(intensity);
    this.turbidity.update(dt, w, h);

    // Protein crystals — sharp shards scattered around the dish that
    // damage on contact. Same biomass schedule as turbidity so the
    // visual murk and the actual hazard ramp together. Skipped while
    // dead so the post-death timer doesn't keep grinding HP into a
    // 0-HP player and re-firing damage events.
    if (this.running) {
      const pc = world.get(this.playerEntity, PlayerCell);
      const ppos = world.get(this.playerEntity, Position);
      this.crystals.setFromBiomass(player.biomass);
      if (pc && ppos) {
        this.crystals.update(dt, w, h, ppos.x, ppos.y, pc.radius);
      }
    }

    // Scoreboard clock — persisted in the run save, so it survives
    // deaths and reloads (StageManager's elapsed restarts at 0 on boot).
    if (this.running || this.deadTimer > 0) saveManager.getRun().scoreTime += dt;

    if (this.running) {
      this.tickIntro(dt);
      this.movement.update(world, dt, vw, vh);
      this.absorption.update(world, dt);
      this.combo.update(dt);
      this.environment.update(dt);
      this.spawn.update(dt, vw, vh);
      this.apexHunt.update(dt, vw, vh);

      this.applyPassives(dt);

      if (player.hp > 0) player.regenHp(player.stats.hpRegen * dt);

      saveManager.getRun().elapsed += dt;
      saveManager.getRun().biomass = player.biomass;
      saveManager.getRun().hp = player.hp;
      saveManager.getRun().atp = player.atp;

      // Win on biomass alone. Time is no longer a gate — it just shapes
      // the carryover bonus. Faster runs = bigger DNA head-start in
      // Phase 2; slow runs still complete, just with less momentum.
      if (player.biomass >= STAGE_TARGET.biomass) {
        this.triggerComplete(saveManager.getRun().scoreTime);
      }
    } else if (this.deadTimer > 0) {
      this.deadTimer -= dt;
      if (this.deadTimer <= 0) this.restart('deadTimer');
    }

    this.particles.update(dt, world);
    this.ui.tick(dt);
  }

  /**
   * Scripted reveal for a brand-new run. The spawn system is held off so
   * the player meets the world one piece at a time: cell, then food, then
   * predator, each with its codex pop. After the last beat, normal spawn
   * resumes as a real bloom.
   */
  private beginIntro(
    vw: number,
    vh: number,
    foodDefs: Record<string, import('@/data/types').FoodDef>,
    predatorDefs: Record<string, import('@/data/types').PredatorDef>,
  ): void {
    this.introMode = true;
    this.introT = 0;
    this.spawn.setEnabled(false);
    const cx = vw / 2, cy = vh / 2;

    // Beats are spaced wide so each codex toast has time to be read and
    // each new element can be observed in stillness before the next one
    // arrives. The petri dish stays empty between beats — that quiet is
    // intentional, it sets the mood and lets the player adjust.
    this.introBeats = [
      // 1.8s: welcome codex pop. The cell is already on screen — let it
      // settle for nearly two seconds before the first toast fires so the
      // player registers "this is me" before reading.
      { at: 1.8, fired: false, fn: () => {
        bus.emit('codex:unlocked', { entryId: 'concept.welcome' });
      } },
      // 4.5s: goal banner. After the welcome toast has been read, state
      // the objective explicitly so the player has a number to chase.
      { at: 4.5, fired: false, fn: () => {
        bus.emit('stage:goalBanner', {
          title: 'Reach 250,000 biomass to evolve',
          subtitle: 'The faster you arrive, the more momentum carries forward.',
          durationSec: 6,
        });
      } },
      // 9.0s: a few proteins drift in near the cell. Codex pops alongside.
      // 7s after the welcome toast — long enough to read the entry and
      // experiment with movement before food appears.
      { at: 9.0, fired: false, fn: () => {
        bus.emit('codex:unlocked', { entryId: 'food.protein' });
        const protein = foodDefs['protein'];
        if (protein) {
          for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2;
            const r = 90 + Math.random() * 30;
            createFood(this.ctx.world, this.entityLayer, protein, cx + Math.cos(a) * r, cy + Math.sin(a) * r);
          }
        }
      } },
      // 19.0s: first predator enters from the edge. Codex pops.
      // 10s on food gives the player time to absorb a few and feel the
      // calm before the threat arrives.
      { at: 19.0, fired: false, fn: () => {
        bus.emit('codex:unlocked', { entryId: 'predator.amoeba' });
        const amoeba = predatorDefs['amoeba'];
        if (amoeba) {
          // spawn from a random edge so it reads as "something arrived"
          const side = Math.floor(Math.random() * 4);
          let sx = 0, sy = 0;
          switch (side) {
            case 0: sx = -50; sy = Math.random() * vh; break;
            case 1: sx = vw + 50; sy = Math.random() * vh; break;
            case 2: sx = Math.random() * vw; sy = -50; break;
            case 3: sx = Math.random() * vw; sy = vh + 50; break;
          }
          createPredator(this.ctx.world, this.entityLayer, amoeba, sx, sy, { hp: 0.6, damage: 0.6 });
        }
      } },
      // 26.0s: open the floodgates — normal spawn resumes from here. 7s
      // alone with the first predator lets the player learn its rhythm
      // before reinforcements arrive.
      { at: 26.0, fired: false, fn: () => {
        this.spawn.setEnabled(true);
        this.spawn.spawnInitial(vw, vh);
        this.introMode = false;
      } },
    ];
  }

  /** Advance scripted intro beats. No-op once the intro has ended. */
  private tickIntro(dt: number): void {
    if (!this.introMode) return;
    this.introT += dt;
    for (const b of this.introBeats) {
      if (!b.fired && this.introT >= b.at) {
        b.fired = true;
        b.fn();
      }
    }
  }

  /** Per-frame passives (mutation-driven biomass + ATP regen). */
  private applyPassives(dt: number): void {
    const { world } = this.ctx;
    const pos = world.get(this.playerEntity, Position);
    const vel = world.get(this.playerEntity, Velocity);
    if (!pos || !vel) return;

    const speed = Math.hypot(vel.vx, vel.vy);
    const stationary = speed < 5;

    const effects = this.mutations.getEffects();
    let passive = effects.passiveBiomassPerSec ?? 0;
    if (stationary && effects.passiveBiomassStationary) {
      passive += effects.passiveBiomassStationary;
    }
    if (passive > 0) {
      player.addBiomass(passive * dt * player.stats.biomassMult);
    }

    let regen = player.stats.atpRegen;
    if (stationary && effects.atpRegenStationaryBonus && effects.atpRegenStationaryBonus > 0) {
      regen *= 1 + effects.atpRegenStationaryBonus;
    }
    player.regenAtp(regen * dt);

    this.lastPlayerX = pos.x;
    this.lastPlayerY = pos.y;
  }

  private triggerComplete(elapsed: number): void {
    console.warn('[CellStage] triggerComplete — biomass=', player.biomass, 'elapsed=', elapsed);
    this.running = false;
    const runCodexCount = saveManager.getRun().codexUnlockedThisRun.length;
    const ownedCount = this.mutations.ownedIds().length;

    // Bonus calc: rewards both speedrunners (speed tier) and explorers
    // (codex bonus). Stored under meta.prestige.carryover for Phase 2 to
    // consume on first stage start. Phase 1 doesn't use it itself, but
    // we save it now so a player who finishes today still gets credit
    // when Phase 2 ships.
    const tier = speedTierFor(elapsed);
    const baseDna = player.dna;
    const speedBonusDna = Math.round(baseDna * (tier.mult - 1));
    // Heritage Tree: trace_memory adds % to the codex DNA carryover.
    const codexBonusMult = 1 + this.metaUpgrades.getStat('codexDnaBonus');
    const codexBonusDna = Math.round(runCodexCount * 5 * codexBonusMult);
    const totalDnaCarry = baseDna + speedBonusDna + codexBonusDna;
    // Biomass head-start: 2% of final biomass, scaled by speed tier.
    // Caps the windfall so it's a meaningful seed without trivializing
    // Phase 2's early game.
    const biomassHeadStart = Math.min(20_000, Math.round(player.biomass * 0.02 * tier.mult));

    saveManager.getMeta().prestige = {
      ...(saveManager.getMeta().prestige ?? {}),
      carryover: {
        fromStage: 'cell',
        completedAt: new Date().toISOString(),
        elapsed,
        speedTier: tier.label,
        speedMult: tier.mult,
        baseDna,
        speedBonusDna,
        codexBonusDna,
        totalDnaCarry,
        biomassHeadStart,
        codexUnlockedTotal: saveManager.getMeta().codexUnlocked.length,
      },
    };
    saveManager.save();

    stageManager.triggerExtinctionEvent({
      biomass: player.biomass,
      dnaEarned: baseDna, // persists across prestige via meta
      mutationsEquipped: ownedCount,
      codexUnlocked: runCodexCount,
      eventsSurvived: saveManager.getRun().eventsSurvived,
      time: elapsed,
      speedTier: tier.label,
      speedMult: tier.mult,
      speedBonusDna,
      codexBonusDna,
      totalDnaCarry,
      biomassHeadStart,
    });
    // After the complete screen is up, so its score line gets the result.
    bus.emit('score:goalReached', { stageId: 'cell' });
  }

  private restart(reason: string = 'unknown'): void {
    // Phase 1 has no prestige yet, so "restart" — whether triggered by death
    // or by the stage-complete "Continue a new run" button — always uses the
    // soft reset. Biomass/HP/ATP/elapsed zero out; upgrades, equipped
    // mutations, and codex unlocks all persist. Previously the stage-complete
    // path wiped everything, which felt like a random progress-loss bug
    // because the screen only fires once you hit 100K biomass + 10 minutes.
    // When Phase 2 lands, introduce a real prestige call site that uses
    // resetRunProgress and converts DNA/codex into permanent tree nodes.
    console.warn('[CellStage] restart triggered — reason:', reason,
      'biomass=', player.biomass, 'hp=', player.hp, 'elapsed=', saveManager.getRun().elapsed);
    bus.emit('fx:floatingText', {
      x: this.worldW / 2,
      y: this.worldH / 2 - 80,
      text: `RESTART: ${reason}`,
      color: 0xff6a3a,
    });
    saveManager.resetAfterDeath('cell');
    this.metaUpgrades.notifyStageStart();
    this.achievements.notifyStageStart('cell');
    const { world, app } = this.ctx;
    world.clear();
    this.entityLayer.removeChildren();
    this.fxLayer.removeChildren();

    // Reuse the existing ParticleSystem — re-creating it leaked bus listeners
    // (old listener kept adding orphan Text nodes to fxLayer). reset() clears
    // live particles/floaters and re-attaches the trail graphic.
    this.particles.reset();
    this.crystals.reset();

    this.upgrades.recompute();
    player.biomass = 0;
    this.metaUpgrades.applyRunStartBonuses();
    player.hp = player.stats.hpMax;
    player.atp = player.stats.atpMax;
    bus.emit('biomass:changed', { value: player.biomass, delta: 0 });
    bus.emit('hp:changed', { value: player.hp, max: player.stats.hpMax });
    bus.emit('atp:changed', { value: player.atp, max: player.stats.atpMax });
    this.combo.reset();
    this.apexHunt.reset();

    const vw = this.worldW;
    const vh = this.worldH;
    this.playerEntity = createCell(world, this.entityLayer, vw / 2, vh / 2);
    this.spawn.spawnInitial(vw, vh);
    this.running = true;
  }

  /**
   * Find a live food/predator entity matching `codexId` whose position
   * lies inside the current screen rect. Used by CodexSpotlight to
   * decide whether the first-encounter popup should fire.
   */
  private findVisibleEntityByCodexId(codexId: string): SpotlightTarget | null {
    const sw = this.worldW;
    const sh = this.worldH;
    const margin = 20;
    // The player's own cell is the subject for the welcome concept.
    if (codexId === 'concept.welcome') {
      const pc = this.ctx.world.get(this.playerEntity, PlayerCell);
      const pos = this.ctx.world.get(this.playerEntity, Position);
      if (!pc || !pos) return null;
      if (pos.x < margin || pos.x > sw - margin || pos.y < margin || pos.y > sh - margin) return null;
      return { x: pos.x, y: pos.y, radius: pc.radius };
    }
    // Food entities (Absorbable + Position) — first match wins.
    for (const e of this.ctx.world.query([Absorbable, Position])) {
      const a = this.ctx.world.get(e, Absorbable);
      if (!a || a.codexId !== codexId) continue;
      const p = this.ctx.world.get(e, Position);
      if (!p) continue;
      if (p.x < margin || p.x > sw - margin || p.y < margin || p.y > sh - margin) continue;
      return { x: p.x, y: p.y, radius: a.radius };
    }
    // Predator entities (Hostile + Position). Margin scales with the
    // predator's radius (+ a small reading buffer) so the popup waits
    // until the body is fully on-screen, not the moment its center
    // crosses the edge.
    for (const e of this.ctx.world.query([Hostile, Position])) {
      const h = this.ctx.world.get(e, Hostile);
      if (!h || h.codexId !== codexId) continue;
      const p = this.ctx.world.get(e, Position);
      if (!p) continue;
      const m = h.radius + 32;
      if (p.x < m || p.x > sw - m || p.y < m || p.y > sh - m) continue;
      return { x: p.x, y: p.y, radius: h.radius };
    }
    return null;
  }

  cleanup(): void {
    for (const r of this.removers) r();
    this.removers = [];
    this.particles?.destroy();
    this.turbidity?.destroy();
    this.crystals?.destroy();
    this.achievements?.destroy();
    this.metaUpgrades?.destroy();
    player.setMetaUpgrades(null);
    this.spotlight?.destroy();
    this.ui?.destroy();
    this.ctx.world.clear();
    this.entityLayer.destroy({ children: true });
    this.fxLayer.destroy({ children: true });
    this.bg.destroy();
    this.flashSprite?.destroy();
    // Detach the chromatic-aberration post-filter from the shared
    // worldLayer. Leaving it on forces the next stage to render through
    // an offscreen target with bilinear sampling — that smears entity
    // edges during scrolling even at uStrength=0.
    this.ctx.worldLayer.filters = [];
  }

  onResize(width: number, height: number): void {
    if (this.bg) { this.bg.width = width; this.bg.height = height; }
    if (this.flashSprite) { this.flashSprite.width = width; this.flashSprite.height = height; }
    // Re-pivot around the new screen center so zoomed-out renders stay centered.
    // Use the dimensions passed in rather than ctx.app.screen.* — on mobile
    // landscape rotation, app.screen can still be stale when this first fires.
    this.applyZoom(this.worldZoom, width, height);
  }

  /** Logical world width. The world always *fills* the screen: zooming
   *  out grows the world instead of shrinking the play field into the
   *  middle of the screen, which is what left the edges unreachable. */
  private get worldW(): number { return this.ctx.app.screen.width / this.worldZoom; }
  private get worldH(): number { return this.ctx.app.screen.height / this.worldZoom; }

  /** Inverse of the entityLayer transform (pivot = world centre, position
   *  = screen centre). */
  private screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const sw = this.ctx.app.screen.width;
    const sh = this.ctx.app.screen.height;
    return {
      x: (sx - sw / 2) / this.worldZoom + sw / this.worldZoom / 2,
      y: (sy - sh / 2) / this.worldZoom + sh / this.worldZoom / 2,
    };
  }

  /** Scale entity/fx layers around the screen center. */
  private applyZoom(z: number, w?: number, h?: number): void {
    z = Math.max(0.5, Math.min(1.5, z));
    this.worldZoom = z;
    const sw = w ?? this.ctx.app.screen.width;
    const sh = h ?? this.ctx.app.screen.height;
    for (const layer of [this.entityLayer, this.fxLayer]) {
      layer.scale.set(z);
      // Pivot on the centre of the *world* (screen / z), so the world
      // covers the screen exactly at any zoom.
      layer.pivot.set(sw / z / 2, sh / z / 2);
      layer.position.set(sw / 2, sh / 2);
    }
  }

  /** Pinch-zoom (mobile): scale the current zoom by `factor`. */
  zoomBy(factor: number): void {
    this.applyZoom(this.worldZoom * factor);
  }

  private triggerFlash(color: number, alpha: number, duration: number): void {
    if (!this.flashSprite) {
      this.flashSprite = new Sprite(Texture.WHITE);
      this.flashSprite.width = this.ctx.app.screen.width;
      this.flashSprite.height = this.ctx.app.screen.height;
      this.flashSprite.alpha = 0;
      this.ctx.app.stage.addChild(this.flashSprite);
    }
    this.flashSprite.tint = color;
    this.flashAlpha = alpha;
    this.flashRemaining = duration;
    this.flashSprite.alpha = alpha;
  }

  // Right-click long-press handle. Tap fires doPulse; held past LONG_PRESS_MS
  // disables the halo (engages the same-type streak combo). On release after
  // a long-press, halo restores to its default-on state.
  private rightHoldTimer: number | null = null;
  private rightWasLongPress = false;
  private readonly LONG_PRESS_MS = 220;

  private setupInput(): void {
    const canvas = this.ctx.app.canvas;
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    let touchDragging = false;

    const isUITarget = (t: EventTarget | null): boolean =>
      !!(t as HTMLElement | null)?.closest?.('#ui .interactive, #ui button, .side-panel, #settings, #ability-pad');

    const updateTarget = (e: PointerEvent) => {
      const { x: wx, y: wy } = this.screenToWorld(e.clientX, e.clientY);
      this.lastPointerX = wx;
      this.lastPointerY = wy;
      this.movement.setPlayerTarget(this.ctx.world, wx, wy);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') {
        // On touch, only follow the finger while it's held down.
        if (!touchDragging) return;
        if (isUITarget(e.target)) return;
        updateTarget(e);
      } else {
        updateTarget(e);
      }
    };
    window.addEventListener('pointermove', onMove);
    this.removers.push(() => window.removeEventListener('pointermove', onMove));

    const onDown = (e: PointerEvent) => {
      if (!this.running) return;
      if (isUITarget(e.target)) return;

      if (e.pointerType === 'touch') {
        // Touch: tap/drag = move only. Ability buttons (DOM) trigger pulse/dash.
        touchDragging = true;
        updateTarget(e);
        return;
      }

      // Mouse / pen: left = dash forward. Right click is dual-purpose:
      // a quick tap = pulse expansion (the classic temporary +ATP cost
      // halo expansion), a long-press = disable halo entirely (so the
      // same-type-streak combo can register). Halo is on by default.
      const { x: wx, y: wy } = this.screenToWorld(e.clientX, e.clientY);
      if (e.button === 0) { e.preventDefault(); this.doDash(wx, wy); }
      if (e.button === 2) {
        e.preventDefault();
        this.rightWasLongPress = false;
        if (this.rightHoldTimer != null) window.clearTimeout(this.rightHoldTimer);
        this.rightHoldTimer = window.setTimeout(() => {
          this.rightWasLongPress = true;
          this.setHalo(false);
          this.rightHoldTimer = null;
        }, this.LONG_PRESS_MS);
      }
      // Middle-click opens the Upgrades panel, which auto-pauses the game
      // through UI.toggle's existing pause wiring. Prevent the browser's
      // default autoscroll-anchor behavior.
      if (e.button === 1) { e.preventDefault(); this.ui.toggle('upgrades'); }
    };
    window.addEventListener('pointerdown', onDown);
    this.removers.push(() => window.removeEventListener('pointerdown', onDown));

    const onUp = (e: PointerEvent) => {
      if (e.pointerType === 'touch') touchDragging = false;
      if (e.button === 2 && e.pointerType !== 'touch') {
        if (this.rightHoldTimer != null) {
          window.clearTimeout(this.rightHoldTimer);
          this.rightHoldTimer = null;
        }
        if (this.rightWasLongPress) {
          // Long-press release — halo returns to its default-on state.
          this.setHalo(true);
        } else {
          // Tap — fire the classic pulse expansion at the cursor.
          this.doPulse(this.lastPointerX, this.lastPointerY);
        }
        this.rightWasLongPress = false;
      }
    };
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    this.removers.push(() => window.removeEventListener('pointerup', onUp));
    this.removers.push(() => window.removeEventListener('pointercancel', onUp));
  }

  /** Touch ability: pulse at cell position (direction irrelevant — it's an AoE). */
  private abilityPulse(): void {
    if (!this.running) return;
    const pos = this.ctx.world.get(this.playerEntity, Position);
    if (!pos) return;
    this.doPulse(pos.x, pos.y);
  }

  /** Touch ability: dash toward the last pointer target (where the cell is moving). */
  private abilityDash(): void {
    if (!this.running) return;
    this.doDash(this.lastPointerX, this.lastPointerY);
  }

  private doPulse(x: number, y: number): void {
    if (!player.spendAtp(25)) return;
    const pc = this.ctx.world.get(this.playerEntity, PlayerCell);
    const pos = this.ctx.world.get(this.playerEntity, Position);
    if (!pc || !pos) return;
    pc.pulseRemaining = 1.0;
    bus.emit('player:pulse', { x, y, radius: pc.absorptionRadius * 2 });
    bus.emit('audio:cue', { id: 'pulse' });
    bus.emit('fx:particles', { x: pos.x, y: pos.y, count: 18, color: 0x4FD1C5, spread: 260 });

    // pulse-damages-predators mutation
    const dmg = this.mutations.getEffects().pulseDamagesPredators ?? 0;
    if (dmg > 0) {
      const rSq = (pc.absorptionRadius * 2) * (pc.absorptionRadius * 2);
      for (const e of this.ctx.world.query([Hostile, Position])) {
        const hp = this.ctx.world.get(e, Position)!;
        const h = this.ctx.world.get(e, Hostile)!;
        const dx = hp.x - pos.x, dy = hp.y - pos.y;
        if (dx * dx + dy * dy <= rSq) h.hp -= dmg;
      }
    }
  }

  private doDash(x: number, y: number): void {
    const effects = this.mutations.getEffects();
    const reduction = Math.min(0.9, effects.dashCostReduction ?? 0);
    const cost = Math.round(30 * (1 - reduction));
    if (!player.spendAtp(cost)) return;
    const pc = this.ctx.world.get(this.playerEntity, PlayerCell);
    const pos = this.ctx.world.get(this.playerEntity, Position);
    if (!pc || !pos) return;
    const dx = x - pos.x, dy = y - pos.y;
    const d = Math.hypot(dx, dy) || 1;
    // Slightly longer than phase 2's dash (~180px → ~340px). Travel = speed * dashRemaining.
    const speed = 1700;
    const dur = 0.20;
    pc.dashVx = (dx / d) * speed;
    pc.dashVy = (dy / d) * speed;
    pc.dashRemaining = dur;
    pc.invulnRemaining = 0.3;
    // Lock the follow target to the dash end-point so the cell doesn't
    // immediately rubber-band back to the cursor position once the dash
    // finishes — the player committed to a forward leap.
    const endX = pos.x + pc.dashVx * dur;
    const endY = pos.y + pc.dashVy * dur;
    pc.targetX = endX;
    pc.targetY = endY;
    this.lastPointerX = endX;
    this.lastPointerY = endY;
    bus.emit('audio:cue', { id: 'dash' });
    bus.emit('fx:particles', { x: pos.x, y: pos.y, count: 10, color: 0x4FD1C5, spread: 180 });
    bus.emit('player:dashed', { x, y, dx: pc.dashVx, dy: pc.dashVy });
  }

  /** Halo defaults on; long-pressing right-mouse turns it off so the
   *  same-type streak combo can register. */
  private setHalo(on: boolean): void {
    const pc = this.ctx.world.get(this.playerEntity, PlayerCell);
    if (!pc) return;
    pc.haloEnabled = on;
    // Reset any in-flight pulse expansion when toggling so the visual
    // matches the new mode immediately.
    pc.pulseRemaining = 0;
  }
}
